import { Controller } from '@hotwired/stimulus'
import * as bootstrap from 'bootstrap'

/**
 * Feedback Modal Controller
 *
 * Handles lazy loading of the feedback modal via Turbo Frame.
 * Supports bug reports, suggestions, and page keyword management.
 * When the button is clicked, it builds the URL with the current page context
 * and loads the modal content from the server.
 */
export default class extends Controller {
  static targets = ['frame']
  static values = {
    url: String,
    keywordableType: String,
    keywordableId: String
  }

  connect() {
    this.modal = null
    this.loaded = false
  }

  open() {
    if (!this.loaded) {
      this._loadFrame()
      // Modal will be shown by frameLoaded() after the frame loads
      return
    }
    this._showModal()
  }

  frameLoaded() {
    this.loaded = true
    this._initializeModal()
    this._showModal()
  }

  _loadFrame() {
    const url = this._buildUrl()
    this.frameTarget.src = url
  }

  _buildUrl() {
    const url = new URL(this.urlValue, window.location.origin)

    // Pass current page context for bug reports
    url.searchParams.set('referrer_path', window.location.pathname)
    url.searchParams.set('referrer_params', window.location.search)

    // Pass keywordable object info if present (for page-keywords feature)
    if (this.hasKeywordableTypeValue && this.keywordableTypeValue) {
      url.searchParams.set('keywordable_type', this.keywordableTypeValue)
    }
    if (this.hasKeywordableIdValue && this.keywordableIdValue) {
      url.searchParams.set('keywordable_id', this.keywordableIdValue)
    }

    return url.toString()
  }

  _initializeModal() {
    const modalElement = this.element.querySelector('#feedback-modal')
    if (modalElement) {
      const bs = this._resolveBootstrapLibrary()
      if (bs?.Modal) {
        this.modal = bs.Modal.getOrCreateInstance(modalElement)
      }
    }
  }

  _showModal() {
    if (this.modal) {
      this.modal.show()
    }
  }

  _resolveBootstrapLibrary() {
    return bootstrap.Modal ? bootstrap : window.bootstrap
  }
}
